import { Record, Map } from "immutable";
import SelectionState from "state/selections/SelectionState";

import Mode from "./Mode";
import {
  COMMIT_SELECTIONS,
  SET_SELECTED_AS_OF_DATE,
  SET_SYMBOLIC_DATE,
  SET_SYMBOLIC_DATE_START,
  GROUP_TYPE_SET,
  GROUP_SET,
  GROUPS_REQUEST,
  GROUPS_RECEIVE,
  SET_SELECTED_AS_OF_DATE_START,
  SET_MODE,
  ADD_ENTITY_ID,
  SET_ENTITY_IDS,
  REQUEST_ENTITIES,
  RECEIVE_ENTITIES,
  REQUEST_ENTITIES_FAILED,
  REQUEST_HOLDING_ATTRIBUTES,
  RECEIVE_HOLDING_ATTRIBUTES,
  REQUEST_SELECTION_SECURITY_ATTRIBUTES,
  RECEIVE_SELECTION_SECURITY_ATTRIBUTES,
  REQUEST_SELECTION_SECURITY_ATTRIBUTES_FAILED,
  REMOVE_ENTITY_ID,
  SET_DISPLAY_RGL,
  SET_IS_SHOW_CANCLED_TRADES,
  SET_REPLACE_MAJORS_WITH_MINORS,
  LOCAL_CURRENCY_FILTER_SET,
  OFFERING_MARKET_FILTER_SET,
  TRANSACTION_CODE_FILTER_SET,
  MAJOR_MINOR_ADJUSTMENT,
  TRANSACTION_EFFECT_FILTER_SET,
  SECURITY_IDS_FILTER_SET,
  CLEAR_REMOVED_SELECTION_FILTERS_DIALOG,
  TICKER_FILTER_SET,
  INSTRUMENT_TYPE_FILTER_SET,
  SET_INSTRUMENT_TYPES_INCLUDE_OPTION,
  ABS_MSAA_SECTOR_FILTER_SET,
  MSAA_BUCKET_FILTER_SET,
  REQUEST_IN2_USERS,
  RECEIVE_IN2_USERS,
  TRADER_FILTER_SET
} from "./actions";
//import MajorMinor from "./MajorMinor";
import TimeSlice from "./TimeSlice";
const Selections = Record({
  mode: Mode.ACC_GROUPS,
  timeSlice: TimeSlice.DAILY,
  asOfDate: null,
  asOfDateStart: null,
  groupType: null,
  groups: null,
  symbolicDate: null,
  symbolicDateStart: null,
  majorMinor: null,
  entityIds: null,
  isDisplayRgl: false,
  localCurrencyCodes: null,
  offeringMarkets: null,
  transactionCodes: null,
  transactionEffects: null,
  tickers: null,
  isShowCanceledTrades: false,
  securityIds: null,
  instrumentTypes: null,
  instrumentTypesIncludeOption: false,
  absMsaaSectors: null,
  msaaBucketCodes: null,
  traderIds: null,
  replaceMajorsWithMinors: true
});

const State = Record({
  fetchingGroupsByGroupTypeMap: Map(),
  fetchingEntities: false,
  entities: null,
  fetchingHoldingAttributes: false,
  holdingAttributes: null,
  groupsByGroupTypeMap: Map(),
  fetchingSelectionSecurityAttributes: false,
  selectionSecurityAttributes: null,
  removedSelectionFiltersDialog: null,
  fetchingIn2Users: false,
  in2Users: null,
  [SelectionState.COMMITTED]: new Selections(),
  [SelectionState.UNCOMMITTED]: new Selections(),
});

const REMOVABLE_SELECTION_FILTERS = [
  {
    stateKey: "securityIds",
    label: "Securities",
    getValue: (securityAttribute) => securityAttribute?.securityId,
  },
  {
    stateKey: "instrumentTypes",
    label: "Instrument Types",
    getValue: (securityAttribute) => securityAttribute?.instrumentType,
  },
  {
    stateKey: "absMsaaSectors",
    label: "ABS MSAA Sectors",
    getValue: (securityAttribute) => securityAttribute?.absMsaaSector,
  },
  {
    stateKey: "msaaBucketCodes",
    label: "MSAA Buckets",
    getValue: (securityAttribute) => securityAttribute?.msaaBucketCode,
  },
];

const createAvailableValueSet = (selectionSecurityAttributes, getValue) => new Set(
  (selectionSecurityAttributes ?? [])
    .map((securityAttribute) => getValue(securityAttribute))
    .filter((value) => value != null)
);

const normalizeSelectionValues = (selectedValues) => {
  if (selectedValues == null) {
    return null;
  }

  return selectedValues.length ? selectedValues : null;
};

const filterSelectedValues = (selectedValues, selectionSecurityAttributes, getValue) => {
  if (selectedValues == null) {
    return selectedValues;
  }

  const availableValues = createAvailableValueSet(selectionSecurityAttributes, getValue);
  return normalizeSelectionValues(
    selectedValues.filter((selectedValue) => availableValues.has(selectedValue))
  );
};

const createRemovedSelectionFiltersDialog = (removedFilterLabels) => {
  if (!removedFilterLabels.length) {
    return null;
  }

  const filtersText = removedFilterLabels.length === 1
    ? removedFilterLabels[0]
    : `${removedFilterLabels.slice(0, -1).join(", ")} and ${removedFilterLabels[removedFilterLabels.length - 1]}`;

  return {
    title: "Security Filters Updated",
    message: `Some selected ${filtersText} were removed because they are not available for the current dates, group & entity selection.`,
  };
};
// This is ES6 Syntax
//return new State() when Redux calls the reducer for the first time with 'undefined' State
//pure function
export default (state = new State(), action) => {
  switch (action.type) {
    case SET_DISPLAY_RGL:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "isDisplayRgl"],
        action.isDisplayRgl
      );

    case SET_IS_SHOW_CANCLED_TRADES:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "isShowCanceledTrades"],
        action.isShowCanceledTrades
      );
    case SET_SELECTED_AS_OF_DATE_START:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "asOfDateStart"],
        action.asOfDateStart
      );
    case SET_SELECTED_AS_OF_DATE:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "asOfDate"],
        action.asOfDate
      );

    case SET_SYMBOLIC_DATE:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "symbolicDate"],
        action.symbolicDate
      );

    case SET_SYMBOLIC_DATE_START:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "symbolicDateStart"],
        action.symbolicDateStart
      );

    case GROUP_TYPE_SET:
      return state.withMutations((s) => {
        s.setIn([SelectionState.UNCOMMITTED, "groupType"], action.groupType);
        s.deleteIn([SelectionState.UNCOMMITTED, "groups"]);
      });

    case GROUP_SET:
      return state.setIn([SelectionState.UNCOMMITTED, "groups"], action.groups);

    case SET_MODE:
      return state.setIn([SelectionState.UNCOMMITTED, "mode"], action.mode);

    case SET_INSTRUMENT_TYPES_INCLUDE_OPTION:
      return state.setIn([SelectionState.UNCOMMITTED, "instrumentTypesIncludeOption"], action.option);

    case LOCAL_CURRENCY_FILTER_SET:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "localCurrencyCodes"],
        action.localCurrencyCodes
      );

    case OFFERING_MARKET_FILTER_SET:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "offeringMarkets"],
        action.offeringMarkets
      );

    case TICKER_FILTER_SET:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "tickers"],
        action.tickers
      );

    case TRANSACTION_CODE_FILTER_SET:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "transactionCodes"],
        action.transactionCodes
      );

    case MAJOR_MINOR_ADJUSTMENT:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "majorMinor"],
        action.majorMinorAdjustment
      );

    case TRANSACTION_EFFECT_FILTER_SET:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "transactionEffects"],
        action.transactionEffects
      );

    case SECURITY_IDS_FILTER_SET:
      return state.withMutations((s) => {
        s.setIn([SelectionState.UNCOMMITTED, "securityIds"], normalizeSelectionValues(action.securityIds));
        s.setIn(["removedSelectionFiltersDialog"], null);
      });

    case CLEAR_REMOVED_SELECTION_FILTERS_DIALOG:
      return state.setIn(["removedSelectionFiltersDialog"], null);

    case INSTRUMENT_TYPE_FILTER_SET:
      return state.withMutations((s) => {
        s.setIn([SelectionState.UNCOMMITTED, "instrumentTypes"], normalizeSelectionValues(action.instrumentType));
        s.setIn(["removedSelectionFiltersDialog"], null);
      });
    case ABS_MSAA_SECTOR_FILTER_SET:
      return state.withMutations((s) => {
        s.setIn([SelectionState.UNCOMMITTED, "absMsaaSectors"], normalizeSelectionValues(action.absMsaaSector));
        s.setIn(["removedSelectionFiltersDialog"], null);
      });

    case MSAA_BUCKET_FILTER_SET:
      return state.withMutations((s) => {
        s.setIn([SelectionState.UNCOMMITTED, "msaaBucketCodes"], normalizeSelectionValues(action.msaaBucketCodes));
        s.setIn(["removedSelectionFiltersDialog"], null);
      });

    case TRADER_FILTER_SET:
      return state.setIn(
        [SelectionState.UNCOMMITTED, "traderIds"],
        action.traderIds

      );

    case SET_REPLACE_MAJORS_WITH_MINORS:
        return state.setIn(
          [SelectionState.UNCOMMITTED, "replaceMajorsWithMinors"],
          action.replaceMajorsWithMinors
        );

    case ADD_ENTITY_ID: {
      return state.withMutations((s) => {
        s.setIn(
          [SelectionState.UNCOMMITTED, "entityIds"],
          s.getIn([SelectionState.UNCOMMITTED, "entityIds"]) ?? []
        );
        s.setIn(
          [SelectionState.UNCOMMITTED, "entityIds"],
          s
            .getIn([SelectionState.UNCOMMITTED, "entityIds"])
            .filter((entityId) => entityId !== action.entityId)
        );
        s.setIn(
          [SelectionState.UNCOMMITTED, "entityIds"],
          [
            ...s.getIn([SelectionState.UNCOMMITTED, "entityIds"]),
            action.entityId,
          ]
        );
      });
    }
    case SET_ENTITY_IDS: {
      return state.withMutations((s) => {
        s.setIn(
          [SelectionState.UNCOMMITTED, "entityIds"],
          action.value ? [...action.value] : []
        );
      });
    }

    case REMOVE_ENTITY_ID: {
      return state.withMutations((s) => {
        s.setIn(
          [SelectionState.UNCOMMITTED, "entityIds"],
          s
            .getIn([SelectionState.UNCOMMITTED, "entityIds"])
            .filter((entityId) => entityId !== action.entityId)
        );
      });
    }

    case GROUPS_REQUEST:
      return state.withMutations((s) => {
        s.setIn(["fetchingGroupsByGroupTypeMap", action.groupType], true);
        s.deleteIn(["groupsByGroupTypeMap", action.groupType]);
      });

    case GROUPS_RECEIVE:
      return state.withMutations((s) => {
        s.setIn(["fetchingGroupsByGroupTypeMap", action.groupType], false);
        s.setIn(["groupsByGroupTypeMap", action.groupType], action.data);
      });

    case REQUEST_ENTITIES: {
      return state.withMutations((s) => {
        s.setIn(["fetchingEntities"], true);
      });
    }
    case RECEIVE_ENTITIES: {
      return state.withMutations((s) => {
        s.setIn(["fetchingEntities"], false);
        s.setIn(["entities"], action.data);
      });
    }

    case REQUEST_ENTITIES_FAILED: {
      return state.withMutations((s) => {
        s.setIn(["fetchingEntities"], false);
      });
    }

    case REQUEST_HOLDING_ATTRIBUTES: {
      return state.withMutations((s) => {
        s.setIn(["fetchingHoldingAttributes"], true);
      });
    }
    case RECEIVE_HOLDING_ATTRIBUTES: {
      return state.withMutations((s) => {
        s.setIn(["fetchingHoldingAttributes"], false);
        s.setIn(["holdingAttributes"], action.data);
      });
    }

    case REQUEST_IN2_USERS: {
      return state.withMutations((s) => {
        s.setIn(["fetchingIn2users"], true);
      });
    }
    case RECEIVE_IN2_USERS: {
      return state.withMutations((s) => {
        s.setIn(["fetchingIn2users"], false);
        s.setIn(["in2Users"], action.data);
      });
    }

    case REQUEST_SELECTION_SECURITY_ATTRIBUTES: {
      return state.withMutations((s) => {
        s.setIn(["fetchingSelectionSecurityAttributes"], true);
        s.setIn(["removedSelectionFiltersDialog"], null);
      });
    }
    case RECEIVE_SELECTION_SECURITY_ATTRIBUTES: {
      return state.withMutations((s) => {
        const removedFilterLabels = [];

        REMOVABLE_SELECTION_FILTERS.forEach(({ stateKey, label, getValue }) => {
          const currentValues = s.getIn([SelectionState.UNCOMMITTED, stateKey]);
          const filteredValues = filterSelectedValues(currentValues, action.data, getValue);

          if ((currentValues?.length ?? 0) > (filteredValues?.length ?? 0)) {
            removedFilterLabels.push(label);
          }

          s.setIn([SelectionState.UNCOMMITTED, stateKey], filteredValues);
        });

        s.setIn(["fetchingSelectionSecurityAttributes"], false);
        s.setIn(["selectionSecurityAttributes"], action.data);
        s.setIn(["removedSelectionFiltersDialog"], createRemovedSelectionFiltersDialog(removedFilterLabels));
      });
    }

    case REQUEST_SELECTION_SECURITY_ATTRIBUTES_FAILED: {
      return state.withMutations((s) => {
        s.setIn(["fetchingSelectionSecurityAttributes"], false);
      });
    }

    case COMMIT_SELECTIONS:
      return state.set(
        SelectionState.COMMITTED,
        state[SelectionState.UNCOMMITTED]
      );

    //If the action is unknown return the previous state
    default:
      return state;
  }
};
